import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../../lib/supabase';
import EditableTable from './EditableTable';

/**
 * Editor for `public.activeness_config` rows where server = selectedServer.
 * 5 levels per server (1 → 5).
 */
const ActivenessTab = ({ server }) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creatingDefaults, setCreatingDefaults] = useState(false);

  const load = useCallback(async () => {
    if (!server) return;
    setLoading(true);
    setError(null);
    const { data, error: dbError } = await supabase
      .from('activeness_config')
      .select('*')
      .eq('server', server)
      .order('level', { ascending: true });
    if (dbError) setError(dbError.message);
    setRows(data || []);
    setLoading(false);
  }, [server]);

  useEffect(() => {
    load();
  }, [load]);

  const columns = [
    {
      key: 'level',
      label: t('superadmin.businessRules.activeness.level', 'Level'),
      type: 'number',
      width: '80px',
      readonly: true,
      render: (v) => (
        <span className="font-data font-semibold text-foreground">L{v}</span>
      ),
    },
    {
      key: 'min_diamonds',
      label: t('superadmin.businessRules.activeness.minDiamonds', 'Min diamonds'),
      type: 'bigint',
    },
    {
      key: 'min_live_days',
      label: t('superadmin.businessRules.activeness.minLiveDays', 'Min LIVE days'),
      type: 'number',
      width: '110px',
    },
    {
      key: 'min_live_hours',
      label: t(
        'superadmin.businessRules.activeness.minLiveHours',
        'Min LIVE hours'
      ),
      type: 'decimal',
      width: '110px',
    },
    {
      key: 'bonus_ratio',
      label: t('superadmin.businessRules.activeness.bonusRatio', 'Bonus %'),
      type: 'decimal',
      width: '110px',
    },
    {
      key: 'bonus_cap',
      label: t('superadmin.businessRules.activeness.bonusCap', 'Bonus cap'),
      type: 'decimal',
      width: '110px',
    },
  ];

  const handleSave = useCallback(
    async (row, patch) => {
      const { error: dbError } = await supabase
        .from('activeness_config')
        .update(patch)
        .eq('id', row.id);
      if (dbError) throw dbError;
      await load();
    },
    [load]
  );

  const handleSeedDefaults = useCallback(async () => {
    if (!server) return;
    if (
      !window.confirm(
        t(
          'superadmin.businessRules.activeness.seedConfirm',
          'Create empty L1→L5 rows for this server?'
        )
      )
    )
      return;
    setCreatingDefaults(true);
    const payload = Array.from({ length: 5 }, (_, i) => ({
      server,
      level: i + 1,
      min_diamonds: 0,
      min_live_days: 0,
      min_live_hours: 0,
      bonus_ratio: 0,
      bonus_cap: null,
    }));
    const { error: dbError } = await supabase
      .from('activeness_config')
      .insert(payload);
    if (dbError) {
      setError(dbError.message);
      window.alert(dbError.message);
    }
    setCreatingDefaults(false);
    await load();
  }, [server, load, t]);

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        {t(
          'superadmin.businessRules.activeness.intro',
          'Activeness levels (1 → 5) per server. Each level requires diamonds AND LIVE days AND LIVE hours together.'
        )}
      </p>
      {error && (
        <div className="rounded-xl border border-error/30 bg-error/5 px-4 py-2 text-sm text-error">
          {error}
        </div>
      )}
      {!loading && rows.length === 0 ? (
        <div className="rounded-2xl bg-card border border-dashed border-border p-8 text-center">
          <p className="text-sm text-muted-foreground mb-3">
            {t(
              'superadmin.businessRules.activeness.notSeeded',
              'No activeness levels configured for this server yet.'
            )}
          </p>
          <button
            type="button"
            onClick={handleSeedDefaults}
            disabled={creatingDefaults}
            className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            {creatingDefaults
              ? t('superadmin.businessRules.saving', 'Saving…')
              : t(
                  'superadmin.businessRules.activeness.seed',
                  'Create L1→L5 default rows'
                )}
          </button>
        </div>
      ) : (
        <EditableTable
          columns={columns}
          rows={rows}
          loading={loading}
          onRowSave={handleSave}
        />
      )}
    </div>
  );
};

export default ActivenessTab;
